import type { AxiosResponse } from 'axios'
import axios from 'axios'
import type {
  Addon,
  BaseAddressDto,
  FindAddressResult,
  GroupItem,
  InsurerInfo,
  InsurerWithPaymentFrequencies,
  Item,
  KadastralAreaInfo,
  SimpleItem,
  Variant,
} from 'BrokerTsLib/src/dtos'
import type { Insurer, Product, SelectionType } from 'brokertslib/src/enums'
import { PackageItemTypeCategory, Risk, RiskVariant } from 'brokertslib/src/enums'
import type {
  GenericQueryOptions
} from 'brokertslib/src/hooks'
import {
  longStaleTime
} from 'brokertslib/src/hooks'
import { useCallback, useMemo } from 'react'
import type { UseQueryResult } from 'react-query'
import { useMutation, useQuery } from 'react-query'
import { useLocation } from 'react-router-dom'

import { Apps } from '@/dtos'
import { Apis, AppSettings } from '../appSettings/AppSettings'
import { useAuth } from '../context/AuthContext'
import { usePartner } from '../context/PartnerContext'
import { useApp } from './useApp'

export const useUrl = (
  apiType: Apis,
  selectedProduct?: Product,
): string | null => {
  const { lastProduct: resolvedProduct } = useApp()
  const { getBaseUrl } = usePartner()
  const api = AppSettings.api[apiType]

  const product = selectedProduct ?? resolvedProduct

  if (typeof api === 'object' && !Array.isArray(api) && api !== null)
    return product ? composeUrl(getBaseUrl(), (api as any)[product]) : null

  return composeUrl(getBaseUrl(), api as string)
}

const composeUrl = (baseUrl: string, apiUrl: string) => `${baseUrl}/${apiUrl}`

export const useSelections = (
  selectionType?: SelectionType,
  options?: GenericQueryOptions<Item[]>,
): UseQueryResult<AxiosResponse<Item[], any>, any> => {
  const url = useUrl(Apis.enum)
  const { product } = useApp()

  return useQuery(
    ['selection', selectionType, product],
    () => axios.get<Item[]>(`${url}/GetSelections?type=${selectionType}`),
    RequiredAuthOpts({ staleTime: longStaleTime, ...options }),
  )
}

export const useInsurers = (
  addonOnly: boolean | null,
  includeInvalid: boolean | null,
  options?: GenericQueryOptions<Item[]>,
  productOverride?: Product | null,
) => {
  const { product: appContextProduct, app } = useApp()
  const product = appContextProduct ?? productOverride
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasEnum : Apis.enum, product!)

  const response = useQuery(
    ['insurer', addonOnly, includeInvalid, url, product],
    () => axios.get<Item[]>(`${url}/GetInsurers?addonProviders=${addonOnly}&includeInvalid=${includeInvalid}`),
    { staleTime: longStaleTime, ...options },
  )

  const isInsurersAvailable = useCallback(
    (insurers: Insurer[]) => (response?.data?.data.filter((x) => insurers.includes(x.id))?.length ?? 0) > 0,
    [response?.data?.data],
  )

  const isInsurerAvailable = useCallback(
    (insurer: Insurer) => isInsurersAvailable([insurer]),
    [isInsurersAvailable],
  )

  return {
    ...response,
    isInsurersAvailable,
    isInsurerAvailable
  }
}

export const useInsurerPaymentFrequencies = (
  options?: GenericQueryOptions<InsurerWithPaymentFrequencies[]>,
): UseQueryResult<AxiosResponse<InsurerWithPaymentFrequencies[], any>, any> => {
  const url = useUrl(Apis.enum)
  const { product } = useApp()

  return useQuery(
    ['InsurerWithPaymentFrequencies', url, product],
    () =>
      axios.get<InsurerWithPaymentFrequencies[]>(
        `${url}/GetInsurerWithAllowedPaymentFrequencies`,
      ),
    { staleTime: longStaleTime, ...options },
  )
}

export const useGetInsurerInfo = (
  insurer: Insurer,
  options?: GenericQueryOptions<InsurerInfo>,
): UseQueryResult<AxiosResponse<InsurerInfo, any>, any> => {
  const url = useUrl(Apis.enum)
  const { product } = useApp()

  return useQuery(
    ['useGetInsurerInfo', insurer, product],
    () => axios.get<InsurerInfo>(`${url}/GetInsurerInfo?insurerId=${insurer}`),
    RequiredAuthOpts({ staleTime: longStaleTime, ...options }),
  )
}

// export const useCountries = (enabled?: boolean, options?: GenericQueryOptions<SimpleItem[]>): UseQueryResult<AxiosResponse<SimpleItem[], any>, any> =>
//     libHooks.useCountries(enabled, RequiredAuthOpts({ staleTime: longStaleTime, enabled, ...options }))

export const useCountries = (
  enabled?: boolean,
  options?: GenericQueryOptions<SimpleItem[]>,
): UseQueryResult<AxiosResponse<SimpleItem[], any>, any> => {
  const url = useUrl(Apis.enum)
  const { product } = useApp()

  return useQuery(['country', product],
    () => axios.get<SimpleItem[]>(`${url}/GetCountries`),
    RequiredAuthOpts({ staleTime: longStaleTime, enabled, ...options }),
  )
}

export const useRegions = (
  postCode: string,
  options?: GenericQueryOptions<BaseAddressDto[]>,
): UseQueryResult<AxiosResponse<BaseAddressDto[], any>, any> => {
  const url = useUrl(Apis.enum)
  const { product } = useApp()

  return useQuery(
    ['region', postCode, product],
    () => axios.get<BaseAddressDto[]>(`${url}/GetRegions?postCode=${postCode}`),
    RequiredAuthOpts({ staleTime: longStaleTime, ...options }),
  )
}

export const useGetRisks = (options?: GenericQueryOptions<GroupItem[]>,): UseQueryResult<AxiosResponse<GroupItem[], any>, any> => {
  const url = useUrl(Apis.enum)
  const { product } = useApp()

  return useQuery(['risk', product],
    () => axios.get<Item[]>(`${url}/GetRisks`), { staleTime: longStaleTime, ...options })
}

export const useGetGroupedRisks = (variant: Variant, ignoreRiskIds?: Risk[]) => {
  const { data } = useGetRisks({ refetchOnWindowFocus: false, refetchOnMount: false })

  const risks = data?.data ?? []
  const typeRiskVariant = variant.variantParts?.find(x => x.risk == Risk.PROPERTY_TYPE)?.riskVariant

  const result = risks
    .filter(x => {
      if (!typeRiskVariant || typeRiskVariant == RiskVariant.HouseholdAndRealty) return true
      if (typeRiskVariant == RiskVariant.Household) return Number(x.groupId) != PackageItemTypeCategory.Realty
      if (typeRiskVariant == RiskVariant.Realty) return Number(x.groupId) != PackageItemTypeCategory.Household

      console.log('Invalid variant', variant)
      return false
    })
    .filter(x => !ignoreRiskIds || !ignoreRiskIds.includes(x.id))
    .reduce((pv, cv) => {
      return {
        ...pv,
        [cv.groupOrder ?? '']: [...(pv[cv.groupOrder ?? ''] ?? []), cv]
      }
    }, {} as { [n: string]: GroupItem[] })

  return result
}

// export const useGetGroupedRisks = () => {
//   const { data } = useGetRisks({
//     refetchOnWindowFocus: false,
//     refetchOnMount: false,
//   })

//   return (data?.data ?? []).reduce(
//     (pv, cv: GroupItem) => {
//       return {
//         ...pv,
//         [cv.groupId ?? '']: [...(pv[cv.groupId ?? ''] ?? []), cv],
//       }
//     },
//     {} as { [n: string]: GroupItem[] },
//   )
// }

export const useGetRiskVariants = (
  risk?: Risk,
  options?: GenericQueryOptions<Item<RiskVariant>[]>,
): UseQueryResult<AxiosResponse<Item<RiskVariant>[], any>, any> => {
  const { product, lastApp: { app } } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasEnum : Apis.enum, product!)

  return useQuery(
    ['riskVariant', risk, product],
    () =>
      axios.get<Item<RiskVariant>[]>(`${url}/GetRiskVariants`, { params: { riskId: risk } }),
    {
      staleTime: longStaleTime,
      ...options,
    },
  )
}

export const useRiskData = (risk: Risk, addons: Addon[], ignoreRiskVariantIds?: RiskVariant[]) => {
  const { data } = useGetRiskVariants(risk)

  const riskVariants = ((ignoreRiskVariantIds ?? []).length > 0)
    ? data?.data.filter(x => !ignoreRiskVariantIds?.includes(x.id))
    : data?.data

  const defaultValue = (riskVariants?.length ?? 0) > 0 ? riskVariants![0] : undefined
  const selected = addons.find(x => x.riskId == risk) ?? { riskId: risk, riskVariantId: defaultValue?.id }
  const value = riskVariants?.find(x => x.id == selected?.riskVariantId)
  const sliderDS = riskVariants?.map((item, index) => { return { i: index, ...item } }) ?? []

  let min = undefined
  let max = undefined
  if ((sliderDS?.length ?? 0) > 0) {
    min = sliderDS[0]
    max = sliderDS[sliderDS.length - 1]
  }

  return {
    selected, value, sliderDS, min, max
  }
}

export const useYearKm = (
  categoryId: number,
  options?: GenericQueryOptions<SimpleItem[]>,
): UseQueryResult<AxiosResponse<SimpleItem[], any>, any> => {
  const url = useUrl(Apis.enum)
  const { product } = useApp()

  return useQuery(
    ['useYearKm', categoryId, product],
    () => axios.get<SimpleItem[]>(`${url}/GetYearKm?categoryId=${categoryId}`),
    { staleTime: longStaleTime, ...options },
  )
}

export const useGetInsurerServiceTypes = (
  insurerId: number,
  options?: GenericQueryOptions<SimpleItem[]>,
): UseQueryResult<AxiosResponse<SimpleItem[], any>, any> => {
  const url = useUrl(Apis.enum)
  const { product } = useApp()

  return useQuery(
    ['useGetInsurerServiceTypes', insurerId, product],
    () =>
      axios.get<SimpleItem[]>(
        `${url}/GetInsurerServiceTypes?insurerId=${insurerId}`,
      ),
    { staleTime: longStaleTime, ...options },
  )
}


export const useFindStreet = (search: string, options?: GenericQueryOptions<string[]>): UseQueryResult<AxiosResponse<string[], any>, any> => {
  const url = useUrl(Apis.enum)
  const { product } = useApp()

  return useQuery(['useFindStreet', search, product],
    () => axios.get<string[]>(`${url}/FindStreet?search=${search}`),
    RequiredAuthOpts({ staleTime: longStaleTime, ...options }))
}

export const useFindAddress = () => {
  const url = useUrl(Apis.enum)

  return useMutation(
    ({ street, searchQuery }: { street?: string, searchQuery?: string }) => axios.get<FindAddressResult[]>(`${url}/FindAddress?street=${street}&searchQuery=${searchQuery}`).then(x => x.data)
  )
}

export const useFindKatUzemi = (zipCode: string, options?: GenericQueryOptions<KadastralAreaInfo[]>): UseQueryResult<AxiosResponse<KadastralAreaInfo[], any>, any> => {
  const url = useUrl(Apis.enum)
  const { product } = useApp()

  return useQuery(['useFindKatUzemi', zipCode, product],
    () => axios.get<KadastralAreaInfo[]>(`${url}/FindKatUzemi?psc=${zipCode}`),
    RequiredAuthOpts({ staleTime: longStaleTime, ...options }))
}

const RequiredAuthOpts = <T>(
  opt: GenericQueryOptions<T>,
): GenericQueryOptions<T> => {
  const ctx = useAuth()

  return { ...opt, enabled: opt?.enabled && ctx?.user?.logged }
}

export const useQueryString = () => {
  const { search } = useLocation()

  return useMemo(() => new URLSearchParams(search), [search])
}
